'use client';

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faGlobe, faChartLine } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import useAnalytics from 'src/hooks/analytics/useAnalytics';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import { useAuth } from 'src/context/auth';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { TableRowActions } from '../';
import {
  Tooltip,
  PulseHeader,
  PulseCell,
  PulseColumnMenu,
  PulseColumnDef,
  PulseLoading,
} from 'src/components';
import { AnalyticsErrorOverlay } from '../AnalyticsErrorOverlay';
import { AnalyticsTableHeader } from '../AnalyticsTableHeader';
import { ANALYTICS_LOCATION } from 'src/hooks/filters/constants';
import {
  useGetAspAnalyticsQuery,
  useGetCompanySettingsQuery,
  useGetCodeByNameQuery,
} from 'src/api/redux';
import { SortSection } from '../AnalyticsTrendsTable/SortSection';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { toApiPeriod } from 'src/utils/quarterUtils';
import { getNewExport } from 'src/api';
import { AnalyticsResultItem } from '../types';
import { ANALYTICS_DEFAULT_DATE_RANGE } from 'src/components/constants';
import { getCodeSetCodeColumns } from '../../utils';

// Type for the ASP data returned from API
type AspResultItem = AnalyticsResultItem<{
  claims_revenue?: Record<string, number>;
  claims_total?: number;
}>;

const AnalyticsAspTable = () => {
  const { user } = useAuth();
  const log = useLogging();
  const { setNotification } = useNotification();

  const { data: companySettings } = useGetCompanySettingsQuery(undefined, {
    skip: !user?.company?.id,
  });

  const {
    selectedGeographyObjects = [],
    selectedCodeSet = [],
    formattedFiltersForAPI,
    date_range = ANALYTICS_DEFAULT_DATE_RANGE,
    granularity = 'quarter',
    periods = [],
    volumeTypes = [],
    modeType,
    volumeConfigs = {},
  } = useAnalytics(ANALYTICS_LOCATION.ASP) || {};

  // Under the `quarterly-ta-data` flag, send the granularity + periods contract.
  // `granularity` lives INSIDE `date_range` (it's a field of DateRangeSerializer)
  // — a top-level granularity is ignored by the backend and silently collapses
  // results to year buckets. Otherwise keep the legacy gte/lte year range.
  const { quarterlyTaData } = useFlags();
  const timeFilter = useMemo(() => {
    if (quarterlyTaData) {
      return {
        date_range: {
          granularity,
          periods: periods.map(toApiPeriod),
        },
      };
    }
    return {
      date_range: {
        gte: parseInt(date_range.gte),
        lte: parseInt(date_range.lte),
      },
    };
  }, [quarterlyTaData, granularity, periods, date_range]);

  const [sort, setSort] = useState<{
    sort: string;
    order: 'asc' | 'desc';
  } | null>({
    sort: 'name',
    order: 'asc',
  });

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    if (sort && sort.sort !== 'name') {
      const currentVolumeType = volumeTypes?.[0] || 'claims';
      if (!sort.sort.includes(currentVolumeType)) {
        setSort({
          sort: 'name',
          order: 'asc',
        });
      }
    }
  }, [volumeTypes, sort]);

  const shouldFetch = useMemo(() => {
    if (!selectedCodeSet) return false;
    return Array.isArray(selectedGeographyObjects) && selectedGeographyObjects.length > 0 && selectedCodeSet?.length > 0;
  }, [selectedGeographyObjects, selectedCodeSet]);

  const apiVolumeConfigs = useMemo(() => {
    if (!selectedCodeSet || selectedCodeSet.length === 0) return {};

    return selectedCodeSet.reduce((acc, codeSet) => {
      const codeSetId = codeSet.id.toString();

      const config = volumeConfigs[codeSetId] || {
        code_set: codeSet.id,
        volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
        mode: modeType || 'treating',
      };

      acc[codeSetId] = config;
      return acc;
    }, {} as Record<string, any>);
  }, [selectedCodeSet, volumeConfigs, volumeTypes, modeType]);

  const {
    currentData: aspData,
    isLoading: isLoadingAsp,
    isFetching: isFetchingAsp,
    isError: isErrorAsp,
    error: errorAsp,
  } = useGetAspAnalyticsQuery(
    {
      params: {
        sort: sort?.sort,
        order: sort?.order,
      },
      data: {
        ...timeFilter,
        volume_configs: apiVolumeConfigs,
        geographies: selectedGeographyObjects.map((g) => ({
          id: g.id,
          type: g.type,
        })),
        ...(formattedFiltersForAPI.length > 0 && {
          filters: formattedFiltersForAPI,
        }),
      },
    },
    {
      skip: !shouldFetch,
    }
  );

  const { closeAllButCurrent } = useDrawer();

  const { dataColumns, dataKeys } = useMemo(() => {
    const codes = getCodeSetCodeColumns(selectedCodeSet);

    return {
      dataColumns: codes,
      dataKeys: codes,
    };
  }, [selectedCodeSet]);

  const hasCodeSets = selectedCodeSet?.length > 0;

  // Define columns using PulseColumnDef for resizing (excluding Geography which is rendered separately)
  const columns: PulseColumnDef[] = useMemo(() => {
    return [
      ...dataColumns.map((col) => ({
        field: col,
        headerName: col,
        width: 200,
        minWidth: 150,
        maxWidth: 400,
        resizable: true,
        sortable: true,
      })),
      ...(hasCodeSets
        ? [
            {
              field: 'total',
              headerName: 'Total',
              width: 200,
              minWidth: 150,
              maxWidth: 400,
              resizable: true,
              sortable: true,
            },
          ]
        : []),
    ];
  }, [dataColumns, hasCodeSets]);

  // Use column resizing hook
  const { startResizing, isResizing, getGridTemplateColumns } =
    usePulseTableColumns({ columns });

  const { currentData: aspCodeData } = useGetCodeByNameQuery(
    { codeIds: dataColumns },
    {
      skip: dataColumns.length === 0,
    }
  );

  // Create a mapping of code names to descriptions
  const codeDescriptions = useMemo(() => {
    if (!aspCodeData?.results) return {};

    return aspCodeData.results.reduce((acc, codeData) => {
      acc[codeData.code_name] = codeData.description;
      return acc;
    }, {} as Record<string, string>);
  }, [aspCodeData]);
  // Determine if all ASP filters are configured
  const allFiltersConfigured = useMemo(() => {
    if (!companySettings?.results || dataKeys.length === 0) {
      return false;
    }

    const aspValuesFromSettings = companySettings.results.find(
      (setting: any) => setting.code === 'asp_values'
    );

    if (!aspValuesFromSettings?.value) {
      return false;
    }

    try {
      const parsedValues =
        typeof aspValuesFromSettings.value === 'string'
          ? JSON.parse(aspValuesFromSettings.value)
          : aspValuesFromSettings.value;

      if (!Array.isArray(parsedValues)) {
        return false;
      }

      const configuredCodes = new Set(
        parsedValues.map(
          (aspValue: { code: string; value: any }) => aspValue.code
        )
      );

      const allConfigured = dataKeys.every((key) => configuredCodes.has(key));

      return allConfigured;
    } catch (error) {
      return false;
    }
  }, [companySettings, dataKeys]);

  const displayedGeographies = useMemo((): AspResultItem[] => {
    const geographies = Array.isArray(selectedGeographyObjects) ? selectedGeographyObjects : [];

    if (aspData?.results && aspData.results.length > 0) {
      const selectedIds = new Set<string | number>(
        geographies.map((g) => g.id)
      );

      const sortedResults = aspData.results.filter((result: AspResultItem) =>
        selectedIds.has(result.id)
      );

      const resultsMap = new Map<string | number, AspResultItem>(
        aspData.results.map((result: AspResultItem) => [result.id, result])
      );

      const missingGeographies = geographies
        .filter((geography) => !resultsMap.has(geography.id))
        .map(
          (geography): AspResultItem => ({
            id: geography.id,
            name: geography.name || '',
            type: geography.type,
          })
        );

      return [...sortedResults, ...missingGeographies];
    }

    return geographies.map(
      (geography): AspResultItem => ({
        id: geography.id,
        name: geography.name || '',
        type: geography.type,
      })
    );
  }, [aspData, selectedGeographyObjects]);

  // Handle table export
  const handleExport = useCallback(
    async (format: 'csv' | 'xlsx') => {
      try {
        // Build query params for the export API
        const queryParams = new URLSearchParams({
          sort: sort?.sort || 'name',
          order: sort?.order || 'asc',
        });

        await getNewExport({
          url: `https://api.medscout.io/api/v1/analytics/territory-insights/average-sale-price/?${queryParams.toString()}`,
          method: 'POST',
          fileFormat: format,
          columns: [
            'id',
            'name',
            'type',
            `${volumeTypes?.[0]}_revenue`,
            `${volumeTypes?.[0]}_total`,
          ],
          fileName: `analytics-average-sale-price-${
            new Date().toISOString().split('T')[0]
          }`,
          data: {
            ...timeFilter,
            volume_configs: apiVolumeConfigs,
            geographies: selectedGeographyObjects.map((g) => ({
              id: g.id,
              type: g.type,
            })),
            ...(formattedFiltersForAPI.length > 0 && {
              filters: formattedFiltersForAPI,
            }),
          },
        });

        setNotification({
          title: 'Success',
          message:
            'Export initiated - You will receive an email when it is ready for download',
          type: 'success',
        });

        log.event('Export', {
          type: ANALYTICS_LOCATION.ASP,
          source: 'Analytics Average Sale Price Table',
          columns: columns.map((col) => col.field),
          count: displayedGeographies.length,
          format,
        });
      } catch (err) {
        log.exception(`Error exporting average sale price: ${err}`);

        setNotification({
          title: 'Error',
          message: err?.error_message || 'Error exporting, please try again.',
          type: 'error',
        });
      }
    },
    [
      columns,
      volumeTypes,
      timeFilter,
      apiVolumeConfigs,
      modeType,
      selectedGeographyObjects,
      formattedFiltersForAPI,
      sort,
      log,
      setNotification,
      displayedGeographies,
    ]
  );

  const handleOpenAspDrawer = useCallback(() => {
    log.event('ASP drawer opened', {
      source: 'AnalyticsAspTable',
    });
    closeAllButCurrent('asp-drawer');
  }, [closeAllButCurrent, log]);

  const handleOpenTerritoryDrawer = useCallback(() => {
    log.event('Territory drawer opened', {
      source: 'AnalyticsAspTable',
    });
    closeAllButCurrent('analytics-territory-drawer');
  }, [closeAllButCurrent, log]);

  const handleEllipsisClick = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.stopPropagation();
      const isOpening = openMenuId !== columnId;
      if (isOpening) {
        log.event('Column menu opened', {
          source: 'AnalyticsAspTable',
          columnId,
        });
      }
      setOpenMenuId((prev) => (prev === columnId ? null : columnId));
    },
    [openMenuId, log]
  );

  const handleSortToggle = useCallback(
    (columnName: string, order?: 'asc' | 'desc') => {
      setSort((prevSort) => {
        const newOrder =
          order ||
          (prevSort?.sort === columnName
            ? prevSort.order === 'asc'
              ? 'desc'
              : 'asc'
            : 'desc');
        log.event('Sort changed', {
          source: 'AnalyticsAspTable',
          columnName,
          sortOrder: newOrder,
        });
        if (order) {
          return {
            sort: columnName,
            order,
          };
        }
        if (prevSort?.sort === columnName) {
          return {
            sort: columnName,
            order: prevSort.order === 'asc' ? 'desc' : 'asc',
          };
        }
        return {
          sort: columnName,
          order: 'desc',
        };
      });
      setOpenMenuId(null);
    },
    [log]
  );

  const hasGeographies = Array.isArray(selectedGeographyObjects) && selectedGeographyObjects.length > 0;
  const hasUsableCodes = dataKeys.length > 0;
  const showGeographyEmptyState = !hasGeographies;
  const showCodeSetEmptyState = !hasCodeSets;
  const showDrugOnlyWarning = hasGeographies && hasCodeSets && !hasUsableCodes;
  const showConfigWarningState =
    hasGeographies && hasCodeSets && hasUsableCodes && !allFiltersConfigured;

  const showLoadingOverlay =
    (isLoadingAsp || isFetchingAsp) &&
    hasGeographies &&
    hasCodeSets &&
    !showDrugOnlyWarning &&
    !showConfigWarningState;

  const totalDataColumns = hasCodeSets
    ? dataColumns.length + 1
    : dataColumns.length;

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden bg-white relative rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),0px_1px_2px_-1px_rgba(0,0,0,0.00)] border border-neutral-200"
      data-testid="analytics-asp-table"
    >
      <AnalyticsTableHeader
        columns={dataColumns}
        showAspButton
        showVolumeToggle={false}
        location={ANALYTICS_LOCATION.ASP}
        onExport={handleExport}
      />

      <div
        className={`flex-1 relative ${
          hasGeographies
            ? 'overflow-x-auto overflow-y-auto'
            : 'overflow-hidden'
        }`}
        data-testid="asp-table-container"
      >
        {/* Full-height separator line between Geography and data columns */}
        {(hasGeographies || showGeographyEmptyState) && (
          <div className="sticky left-[271px] top-12 h-[calc(100%-3rem)] w-px bg-neutral-200 z-[21] pointer-events-none float-left -ml-px" />
        )}

        <div
          role="table"
          aria-label="Analytics ASP data by geography"
          className="grid relative z-10 w-full"
          data-testid="asp-table"
          style={{
            gridTemplateColumns:
              totalDataColumns === 0
                ? '272px 1fr'
                : `272px ${getGridTemplateColumns()}`,
            gridAutoFlow: 'row',
          }}
        >
          <PulseHeader
            label="Geography"
            isSticky
            stickyPosition="both"
            align="left"
            className="z-30 bg-neutral-50 text-neutral-900 border-b border-r border-b-neutral-200 border-r-neutral-200"
            sortable
            sortDirection={sort?.sort === 'name' ? sort.order : null}
            resizable={false}
            menu={
              <PulseColumnMenu
                columnId="name"
                isOpen={openMenuId === 'name'}
                onToggle={(e) => handleEllipsisClick(e, 'name')}
                customSectionComponents={[
                  <SortSection
                    key="sort"
                    currentOrder={sort?.order || null}
                    isActive={sort?.sort === 'name'}
                    onChange={(order) => handleSortToggle('name', order)}
                  />,
                ]}
              />
            }
          />

          {dataColumns.map((col, i) => {
            const sortValue = `${volumeTypes?.[0] || 'claims'}_revenue.${
              dataKeys[i]
            }`;
            const isMenuOpen = openMenuId === sortValue;
            const isActiveSortColumn = sort?.sort === sortValue;
            const description = codeDescriptions[col];

            return (
              <PulseHeader
                key={dataKeys[i]}
                label={
                  <div className="flex items-center gap-2">
                    <span>{col}</span>
                    {description && (
                      <Tooltip
                        content={description}
                        position="top"
                        slotProps={{
                          contentClassName: 'max-w-xs',
                        }}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <Icon
                            icon={faInfoCircle}
                            className="w-4 h-4 text-secondary"
                          />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                }
                isSticky
                stickyPosition="top"
                align="left"
                className="z-20 bg-neutral-50 border-b border-r border-b-neutral-200 border-r-neutral-200"
                sortable
                sortDirection={isActiveSortColumn ? sort.order : null}
                resizable={true}
                onStartResize={(e) => startResizing(col, e)}
                isResizing={isResizing}
                menu={
                  <PulseColumnMenu
                    columnId={sortValue}
                    isOpen={isMenuOpen}
                    onToggle={(e) => handleEllipsisClick(e, sortValue)}
                    customSectionComponents={[
                      <SortSection
                        key="sort"
                        currentOrder={sort?.order || null}
                        isActive={isActiveSortColumn}
                        onChange={(order) => handleSortToggle(sortValue, order)}
                      />,
                    ]}
                  />
                }
              />
            );
          })}

          {hasCodeSets && (
            <PulseHeader
              label="Total"
              isSticky
              stickyPosition="top"
              align="left"
              className="z-20 bg-neutral-50 border-b border-r border-b-neutral-200 border-r-neutral-200"
              sortable
              sortDirection={
                sort?.sort === `${volumeTypes?.[0] || 'claims'}_total`
                  ? sort.order
                  : null
              }
              resizable={true}
              onStartResize={(e) => startResizing('total', e)}
              isResizing={isResizing}
              menu={
                <PulseColumnMenu
                  columnId={`${volumeTypes?.[0] || 'claims'}_total`}
                  isOpen={
                    openMenuId === `${volumeTypes?.[0] || 'claims'}_total`
                  }
                  onToggle={(e) =>
                    handleEllipsisClick(
                      e,
                      `${volumeTypes?.[0] || 'claims'}_total`
                    )
                  }
                  customSectionComponents={[
                    <SortSection
                      key="sort"
                      currentOrder={sort?.order || null}
                      isActive={
                        sort?.sort === `${volumeTypes?.[0] || 'claims'}_total`
                      }
                      onChange={(order) =>
                        handleSortToggle(
                          `${volumeTypes?.[0] || 'claims'}_total`,
                          order
                        )
                      }
                    />,
                  ]}
                />
              }
            />
          )}

          {/* Filler Column Header - empty cell to fill remaining space */}
          <div className="bg-neutral-50 border-b border-b-neutral-200 sticky top-0 z-20" />

          {/* Body of the table */}
          {showGeographyEmptyState && (
            <React.Fragment>
              <div className="flex sticky left-0 items-center justify-center w-[272px] h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Icon icon={faGlobe} className="text-2xl" />
                  <span>Select Geographies</span>
                  <button
                    type="button"
                    onClick={handleOpenTerritoryDrawer}
                    className="px-4 py-2 bg-white text-secondary border border-neutral-200 text-sm font-medium font-['Inter'] rounded-full hover:bg-neutral-100 transition-all duration-300"
                  >
                    Add Geographies
                  </button>
                </div>
              </div>
            </React.Fragment>
          )}
          {/* Code set empty state - State 1: Part of grid when no geographies */}
          {showGeographyEmptyState && showCodeSetEmptyState && (
            <div
              className="flex items-center justify-center bg-white h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
              style={{ gridColumn: '2 / -1' }}
            >
              <div className="flex flex-col items-center justify-center gap-2">
                <Icon icon={faChartLine} className="text-2xl" />
                <span>Select Code Sets to see ASP data</span>
              </div>
            </div>
          )}

          {hasGeographies &&
            displayedGeographies.map((geography) => {
              const isRegion = geography.type === 'region';

              return (
                <React.Fragment key={geography.id}>
                  <PulseCell
                    isSticky
                    stickyPosition="left"
                    className="z-30 border-b border-b-neutral-200 bg-white group"
                  >
                    <div className="flex items-center justify-between w-full gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          data-testid={`geography-name-${geography.id}`}
                          className="truncate text-secondary text-sm font-normal font-['Inter'] leading-5"
                          title={geography.name}
                        >
                          {geography.name}
                        </span>
                        {isRegion && (
                          <Tooltip
                            content="This is a region"
                            slotProps={{
                              containerClassName: '!bg-neutral-900 !text-white',
                            }}
                          >
                            <span
                              className="inline-flex items-center text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded-full cursor-default shrink-0"
                              data-testid={`region-badge-${geography.id}`}
                              role="status"
                              aria-label="Region indicator"
                            >
                              Region
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <TableRowActions
                        geographyId={geography.id}
                        location={ANALYTICS_LOCATION.ASP}
                        isRegion={isRegion}
                      />
                    </div>
                  </PulseCell>

                {dataColumns.map((_, i) => {
                  const key = dataKeys[i];
                  const value = geography?.claims_revenue?.[key];
                  const formattedValue =
                    value != null
                      ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(Number(value))
                      : 'Configure ASP';
                  return (
                    <PulseCell
                      key={dataKeys[i]}
                      align="left"
                      className={`bg-white ${hasCodeSets ? 'border-b border-r border-b-neutral-200 border-r-neutral-200' : ''}`}
                    >
                      {hasCodeSets ? formattedValue : null}
                    </PulseCell>
                  );
                })}

                {hasCodeSets && (
                  <PulseCell
                    align="left"
                    className="bg-white border-b border-r border-b-neutral-200 border-r-neutral-200"
                  >
                    {geography?.claims_total != null
                      ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(Number(geography.claims_total))
                      : '$0'}
                  </PulseCell>
                )}

                {/* Filler Column Cell - empty cell to fill remaining space */}
                <div className={`bg-white ${hasCodeSets ? 'border-b border-b-neutral-200' : ''}`} />
              </React.Fragment>
              );
            })}
        </div>

        {/* Code Set Empty State Overlay - State 2: Shows when we have geographies but no code sets */}
        {!showGeographyEmptyState && showCodeSetEmptyState && (
          <div
            className="absolute left-72 top-12 right-0 bottom-0 z-30 bg-white flex items-center justify-center text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
            data-testid="code-set-empty-state-overlay"
          >
            <div className="flex flex-col items-center justify-center gap-2">
              <Icon icon={faChartLine} className="text-2xl" />
              <span>Select Code Sets to see ASP data</span>
            </div>
          </div>
        )}

        {/* Drug Only Warning Overlay */}
        {showDrugOnlyWarning && (
          <div
            className="absolute left-72 top-12 right-0 bottom-0 z-30 bg-white flex items-center justify-center text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
            data-testid="asp-drug-only-warning-overlay"
          >
            <div className="flex flex-col items-center gap-2 max-w-md text-center">
              <Icon icon={faChartLine} className="text-2xl" />
              <p className="text-sm text-neutral-700">
                There are no procedure or diagnosis codes in this code set.
                Please select a code set with procedure or diagnosis codes.
              </p>
            </div>
          </div>
        )}

        {/* Config Warning Overlay */}
        {showConfigWarningState && (
          <div
            className="absolute left-72 top-12 right-0 bottom-0 z-30 bg-white flex items-center justify-center text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
            data-testid="asp-config-warning-overlay"
          >
            <div className="flex flex-col items-center gap-2 max-w-md text-center">
              <Icon icon={faChartLine} className="text-2xl" />
              <p className="text-sm text-neutral-700">
                Please configure average sale price for this code set to see
                details
              </p>
              <button
                type="button"
                onClick={handleOpenAspDrawer}
                className="px-4 py-2 bg-white text-secondary border border-neutral-200 text-sm font-medium font-['Inter'] rounded-full hover:bg-neutral-100 transition-all duration-300"
              >
                Configure
              </button>
            </div>
          </div>
        )}

        {/* Error Overlay */}
        <AnalyticsErrorOverlay
          error={errorAsp}
          isVisible={isErrorAsp && !isLoadingAsp && !isFetchingAsp}
        />

        {showLoadingOverlay && <PulseLoading />}
      </div>
    </div>
  );
};

export default AnalyticsAspTable;
